import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget for the login CAPTCHA. Renders the challenge and
 * hands the current attempt's token back via `onToken` (null on expiry/error).
 *
 * Tokens are SINGLE-USE: after a submit, change this component's React `key` to
 * remount it and get a fresh token. The Turnstile script is loaded once, lazily.
 */
declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
        }
      ) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/** Load the Turnstile script once and resolve when `window.turnstile` is ready
 * (the load event can fire a tick before the global is assigned, so poll). */
function loadScript(): Promise<void> {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve();
    const whenReady = () => {
      if (window.turnstile) return resolve();
      const t = setInterval(() => {
        if (window.turnstile) {
          clearInterval(t);
          resolve();
        }
      }, 40);
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", whenReady, { once: true });
      whenReady();
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener("load", whenReady, { once: true });
    document.head.appendChild(s);
  });
}

export function Turnstile({
  siteKey,
  onToken,
}: {
  siteKey: string;
  onToken: (token: string | null) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !ref.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        "error-callback": () => onToken(null),
        "expired-callback": () => onToken(null),
        "timeout-callback": () => onToken(null),
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* widget already removed */
        }
      }
    };
    // onToken is a stable setState updater; only re-render on siteKey change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={ref} className="turnstile" style={{ marginTop: 8, display: "flex", justifyContent: "center" }} />;
}
